import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { PatientRecordSearchResult } from '@ctw/shared/api/patient-record-search/patient-record-search';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import type { LegacyOnTableRowClickArgs } from '@ctw/shared/components/tables/legacy-table';
import { FeedbackForm } from '@ctw/shared/content/patient-record-search/feedback-form';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import type { Resource } from 'fhir/r4';
import type { JSX } from 'react';

import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';

type ResourceRowProps = {
  document: PatientRecordSearchResult['document'];
};

export function SearchResultRow(props: ResourceRowProps) {
  const { document } = props;
  const { resource, ...result } = document;
  const resourceType = result.metadata.resource_type;

  const { openResourceDrawer } = useResourceDrawer();

  switch (resourceType) {
    case 'AllergyIntolerance': {
      const allergy = resource as AllergyModel;
      return (
        <SearchResult
          heading={allergy.display}
          label="Allergy"
          resource={allergy}
          openDetails={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          details={[
            { label: 'Last Updated', value: allergy.recordedDate },
            { label: 'Reaction', value: allergy.manifestations },
          ]}
        />
      );
    }
    case 'Condition': {
      const condition = resource as ConditionModel;
      return (
        <SearchResult
          heading={condition.display}
          label="Condition"
          resource={condition}
          openDetails={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          details={[
            {
              label: 'Last Updated',
              value: condition.recordedDate
                ? `${condition.recordedDate} ${condition.recorder ?? ''}`
                : 'Unknown',
            },
            {
              label: 'Earliest Known Onset',
              value: condition.onset,
            },
          ]}
        />
      );
    }

    case 'DocumentReference': {
      const doc = resource as DocumentModel;
      return (
        <SearchResult
          heading={doc.title}
          label="Document"
          resource={doc}
          openDetails={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          text={result.page_content}
          details={[
            {
              label: 'Encounter',
              value: doc.encounterDate,
            },
            {
              label: 'Retrieved',
              value: doc.dateCreated,
            },
            {
              label: 'Author',
              value: doc.custodian,
            },
          ]}
        />
      );
    }

    case 'MedicationStatement': {
      const medication = resource as MedicationStatementModel;
      return (
        <SearchResult
          heading={medication.display}
          label="Medication"
          resource={medication}
          openDetails={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          details={[
            {
              label: 'Dosage',
              value: medication.dosage,
            },
            {
              label: 'Last Filled',
              value: medication.lastFillDate,
            },
            {
              label: 'Last Prescribed',
              value: medication.lastPrescribedDate
                ? `${medication.lastPrescribedDate} ${medication.lastPrescriber}`
                : undefined,
            },
          ]}
        />
      );
    }

    case 'DiagnosticReport': {
      const diagnosticReport = resource as DiagnosticReportModel;
      return (
        <SearchResult
          heading={diagnosticReport.displayName}
          label="Diagnostic Report"
          resource={diagnosticReport}
          openDetails={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          details={[
            {
              label: 'Date',
              value: diagnosticReport.dateDisplay,
            },
            {
              label: '',
              value: diagnosticReport.numResultsDisplay,
            },
          ]}
        />
      );
    }

    default:
      // Unknown resource type
      return null;
  }
}

type SearchResultProps<TResource extends FHIRModel<Resource>> = {
  resource: TResource;
  heading?: string;
  label: string;
  openDetails?: (args: LegacyOnTableRowClickArgs<TResource>) => void;
  text?: string | JSX.Element;
  details?: Array<{ label: string; value?: string }>;
};

function SearchResult<TResource extends FHIRModel<Resource>>({
  resource,
  heading,
  openDetails,
  label,
  text,
  details,
}: SearchResultProps<TResource>) {
  const telemetry = useTelemetry();
  return (
    <div className={tw`patient-record-search-result`}>
      <div className={tw`flex items-end justify-between`}>
        <span>
          <Button
            type="button"
            variant="unstyled"
            className={tw`patient-record-search-result-btn`}
            onClick={() => openDetails?.({ telemetry, record: resource })}
            telemetryObfuscatedIdentifier={`${resource.resourceType}/${resource.id} TITLE`}
          >
            {heading}
          </Button>
          <Badge variant="primary" content={label} />
        </span>
        <FeedbackForm resource={resource} />
      </div>
      <div className={tw`patient-record-search-text`}>{text}</div>

      <div className={tw`patient-record-search-details`}>
        {details?.map((detail) => (
          <div key={detail.label} data-label={detail.label}>
            {detail.value}
          </div>
        ))}
      </div>
    </div>
  );
}
